import { asPlatforms } from "../shared/platforms";
import type { BrowseFilter, FilterRange, List, ListFilter, PitchingEligibility, Platform } from "../shared/types";

export const BROWSE_PLATFORMS: Platform[] = ["TikTok", "Instagram", "YouTube"];

const DEFAULT_PCT_RANGE = { lower: 0, upper: 100 };

export const emptyBrowseFilter: BrowseFilter = {
    contentLanguage: [],
    gender: [],
    favoriteGenre: [],
    contentTags: [],
    tagFilterLogic: "OR",
    cities: [],
    countries: [],
    maleFollowerPctRange: { ...DEFAULT_PCT_RANGE },
    femaleFollowerPctRange: { ...DEFAULT_PCT_RANGE },
    topFollowerCountries: [],
    topFollowerAgeBrackets: [],
    emailInBio: "Any",
    campaignType: "Song",
    currency: "USD",
    search: "",
    listId: "",
    sortBy: "tt-views-desc",
};

function rangeIsStored(range: FilterRange | undefined): range is FilterRange {
    return range != null && (range.lower != null || range.upper != null);
}

export function getClearedBrowseFilter(value: BrowseFilter): BrowseFilter {
    return {
        ...emptyBrowseFilter,
        campaignType: value.campaignType,
        currency: value.currency,
        search: value.search,
        listId: value.listId,
        sortBy: value.sortBy,
        showCampaignTypeSelect: value.showCampaignTypeSelect,
        lockCampaignPlatform: value.lockCampaignPlatform,
        lockCampaignType: value.lockCampaignType,
        lockCurrencyFromBooker: value.lockCurrencyFromBooker,
        lockCurrencyFromList: value.lockCurrencyFromList,
        lockTalentCards: value.lockTalentCards,
    };
}

function hasNestedPlatformFilters(filter: ListFilter | undefined): boolean {
    if (!filter) {
        return false;
    }
    return BROWSE_PLATFORMS.some((platform) => platform in filter);
}

export function syncBrowseFilterPlatforms(value: BrowseFilter, platforms: Platform[]): BrowseFilter {
    const platformSet = new Set(platforms);
    const next: BrowseFilter = { ...value };
    for (const platform of BROWSE_PLATFORMS) {
        if (!platformSet.has(platform)) {
            delete next[platform];
        }
    }
    return next;
}

export function getBrowseFilterFromList(list: Pick<List, "filter" | "platform">, platforms: Platform[]): BrowseFilter {
    const filter = list.filter;
    const next: BrowseFilter = {
        ...emptyBrowseFilter,
        contentLanguage: filter?.contentLanguage ?? [],
        gender: filter?.gender ?? [],
        favoriteGenre: filter?.favoriteGenre ?? [],
        contentTags: filter?.contentTags ?? [],
        tagFilterLogic: filter?.tagFilterLogic === "AND" ? "AND" : "OR",
        cities: filter?.cities ?? [],
        countries: filter?.countries ?? [],
        maleFollowerPctRange: filter?.maleFollowerPctRange ?? { ...DEFAULT_PCT_RANGE },
        femaleFollowerPctRange: filter?.femaleFollowerPctRange ?? { ...DEFAULT_PCT_RANGE },
        topFollowerCountries: filter?.topFollowerCountries ?? [],
        topFollowerAgeBrackets: filter?.topFollowerAgeBrackets ?? [],
        emailInBio: filter?.emailInBio ?? "Any",
        campaignType: filter?.campaignType ?? "Song",
        currency: filter?.currency ?? "USD",
        search: filter?.search ?? "",
        listId: filter?.listId ?? "",
        sortBy: filter?.sortBy ?? emptyBrowseFilter.sortBy,
    };
    if (filter?.showCampaignTypeSelect != null) next.showCampaignTypeSelect = filter.showCampaignTypeSelect;
    if (filter?.lockCampaignPlatform != null) next.lockCampaignPlatform = filter.lockCampaignPlatform;
    if (filter?.lockCampaignType != null) next.lockCampaignType = filter.lockCampaignType;
    if (filter?.lockCurrencyFromBooker != null) next.lockCurrencyFromBooker = filter.lockCurrencyFromBooker;
    if (filter?.lockCurrencyFromList != null) next.lockCurrencyFromList = filter.lockCurrencyFromList;
    if (filter?.lockTalentCards != null) next.lockTalentCards = filter.lockTalentCards;

    if (hasNestedPlatformFilters(filter)) {
        for (const platform of BROWSE_PLATFORMS) {
            if (filter?.[platform]) {
                next[platform] = {
                    followersRange: filter[platform]?.followersRange ?? null,
                    priceRange: filter[platform]?.priceRange ?? null,
                };
            }
        }
        return syncBrowseFilterPlatforms(next, platforms);
    }

    const leftoverFollowers = rangeIsStored(filter?.followersRange) ? filter.followersRange : undefined;
    const leftoverPrice = rangeIsStored(filter?.priceRange) ? filter.priceRange : undefined;
    if (!leftoverFollowers && !leftoverPrice) {
        return next;
    }
    for (const platform of asPlatforms(list.platform)) {
        next[platform] = {
            ...(leftoverFollowers ? { followersRange: { lower: leftoverFollowers.lower ?? null, upper: leftoverFollowers.upper ?? null } } : {}),
            ...(leftoverPrice ? { priceRange: { lower: leftoverPrice.lower ?? null, upper: leftoverPrice.upper ?? null } } : {}),
        };
    }
    return next;
}

export function getBrowseFilterFromEligibility(eligibility?: PitchingEligibility | null): BrowseFilter {
    const next: BrowseFilter = {
        ...emptyBrowseFilter,
        contentLanguage: eligibility?.contentLanguage ?? [],
        gender: eligibility?.gender ?? [],
        favoriteGenre: eligibility?.favoriteGenre ?? [],
        contentTags: eligibility?.contentTags ?? [],
        tagFilterLogic: eligibility?.tagFilterLogic === "AND" ? "AND" : "OR",
        cities: eligibility?.cities ?? [],
        countries: eligibility?.countries ?? [],
        maleFollowerPctRange: eligibility?.maleFollowerPctRange ?? { ...DEFAULT_PCT_RANGE },
        femaleFollowerPctRange: eligibility?.femaleFollowerPctRange ?? { ...DEFAULT_PCT_RANGE },
        topFollowerCountries: eligibility?.topFollowerCountries ?? [],
        topFollowerAgeBrackets: eligibility?.topFollowerAgeBrackets ?? [],
        emailInBio: eligibility?.emailInBio ?? "Any",
    };
    for (const platform of BROWSE_PLATFORMS) {
        const followersRange = eligibility?.followersByPlatform?.[platform];
        if (followersRange) {
            next[platform] = { followersRange };
        }
    }
    return next;
}

export function getEligibilityFromBrowseFilter(value: BrowseFilter): PitchingEligibility {
    const followersByPlatform: NonNullable<PitchingEligibility["followersByPlatform"]> = {};
    for (const platform of BROWSE_PLATFORMS) {
        if (value[platform]?.followersRange) {
            followersByPlatform[platform] = {
                lower: value[platform]?.followersRange?.lower ?? null,
                upper: value[platform]?.followersRange?.upper ?? null,
            };
        }
    }
    return {
        contentLanguage: value.contentLanguage,
        gender: value.gender,
        favoriteGenre: value.favoriteGenre,
        contentTags: value.contentTags,
        tagFilterLogic: value.tagFilterLogic,
        cities: value.cities,
        countries: value.countries,
        maleFollowerPctRange: value.maleFollowerPctRange,
        femaleFollowerPctRange: value.femaleFollowerPctRange,
        topFollowerCountries: value.topFollowerCountries,
        topFollowerAgeBrackets: value.topFollowerAgeBrackets,
        emailInBio: value.emailInBio,
        followersByPlatform,
    };
}
